import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, Inject, Input, OnDestroy, OnInit, Output, ViewChild } from "@angular/core";
import { TemplateTypeId } from "ng/src/app/templates/shared/template.model";
import { ConfirmationService, MenuItem } from "primeng/api";
import { LogWithTemplates } from "../../logs/log.model";
import { defaultIfEmpty, filter, finalize, switchMap, takeUntil, tap } from "rxjs/operators";
import { combineLatest, EMPTY, merge, Subject } from "rxjs";
import { NoteEntryService } from "../shared/note-entry.service";
import { AutoSyncType, EditorMode, NoteEntryStartConfig, NoteStatusUpdate, SectionValidationStatus, SegmentTreeNode, SyncType, TocMode } from "ng/src/app/notes/shared/note-entry.model";
import { Note } from "ng/src/app/notes/shared/note.model";
import { TreeTable } from "primeng/treetable";
import { PrimeNGHelpersService } from "ng/src/app/core/services/primeng-helpers.service";
import { LogBookRootScopeService } from "app/shared/model/app.model";
import { IAngularEvent } from "angular";
import { LogService } from "ng/src/app/logs/log.service";
import { AppStateService } from "ng/src/app/core/services/app-state.service";
import { ResizeObserverHelper } from "ng/src/app/core/resize-observer-helper";
import { Utils } from "ng/src/app/core/utils";

@Component({
    selector: 'lb-note-entry-toc',
    templateUrl: './note-entry-toc.component.html',
    styleUrls: ['./note-entry-toc.component.scss'],
    providers: [NoteEntryService]
})
export class NoteEntryTocComponent implements OnInit, AfterViewInit, OnDestroy {
    
	@Output() notePublished = new EventEmitter<Note | null>(false);
	@Output() backClicked = new EventEmitter<TocMode>();
	@Output() onAccessRevoked = new EventEmitter();
    
    readonly EditorModes = EditorMode;
    readonly TocModes = TocMode;
    readonly TemplateTypes = TemplateTypeId;
	readonly navigationGuardKey = Utils.generateUniqueId();
	
    menu: MenuItem[] = [];
    showSections = false;
    showFieldsMissing = false;
    editorMode: EditorMode = EditorMode.EDITING;
	displayingNavModal = false;
	accessRevokedVisible = false;
	restrictSection: number | undefined;
	syncFeedbackVisible = false;
	syncObjectJSON: string = '';
	showNowPublishedMessage = false;
	publishingNote = false;
	trapOnNavigation = true;

    @ViewChild('toc') tocTable?: TreeTable;
    
    @Input()
    get allowMultiLog() {
        return this.noteEntryService.allowMultiLog;
    }
    set allowMultiLog(val: boolean) {
        this.noteEntryService.allowMultiLog = val;
    };
    
	@Input('fromNote')
	set note(note: Note.Projections.TemplateValues) {
        this.noteEntryService.editNote(note);
    }

	@Input('sectionId')
	set sectionId(val: number | null) {
		if (val == null) return;
		this.noteEntryService.editSectionId(val);
	}
	
	@Input() startConfig: NoteEntryStartConfig | null = null;

    private readonly destroySubject = new Subject();
	readonly onDestroy$ = this.destroySubject.asObservable();
    

    get selectedLog() {
        return this.noteEntryService.selectedLog;
    }
    set selectedLog(val) {
		this.noteEntryService.setLog(val).subscribe();
    }
	get selectedLog$() { 
		return this.noteEntryService.selectedLog$;
	}

    
    @Input('fromTemplate')
    get selectedTemplate() {
        return this.noteEntryService.selectedTemplate;
    }
    set selectedTemplate(val) {
		this.noteEntryService.setTemplate(val).subscribe();
    }
	get selectedTemplate$() { 
		return this.noteEntryService.selectedTemplate$; 
	}

    
    get templateType() {
        return this.selectedTemplate?.templateType.id ?? TemplateTypeId.OPERATIONAL;
    }
    
    get logs() {
        return this.noteEntryService.logs;
    }
    
    get templates() {
        return (this.selectedLog as LogWithTemplates)?.templates ?? [];
    }

    private _selectedNode?: SegmentTreeNode;
    get selectedNode() {
        return this._selectedNode;
    }
    
    get selectedNodeId() {
        return this.selectedNode?.data?.id ?? -1;
    }
    
    get isReviewing() {
        return this.editorMode === EditorMode.REVIEWING && this.mode === TocMode.EDITOR;
    }
    
    get validationStatus() {
        return this.noteEntryService.noteStatus;
    }
    
    get publishAllowed(): boolean {
        return this.validationStatus !== SectionValidationStatus.MISSING_DATA || !!this.selectedTemplate?.allowSubmitIncomplete;
    }
    
    get noteHasIssues() {
        return this.validationStatus !== SectionValidationStatus.OK;
    }
    
    get issueNodes() {
        return this.noteEntryService.issueNodes;
    }

	get syncErrorFeedback() {
		return this.noteEntryService.syncErrorFeedback;
	}

	get hasPendingChanges() {
		return this.noteEntryService.hasPendingChanges;
	}
	set hasPendingChanges(val) {
		this.noteEntryService.hasPendingChanges = val;
	}

	get canReviewNote() {
		return this.templateType === this.TemplateTypes.INSPECTION && !this.editingPublishedNote && !this.isReviewing && !!(this.noteEntryService.editingNote || this.hasPendingChanges);
	}
	
	get publishEnabled() {
		return !(this.noteEntryService.isSyncingNote || this.publishingNote ||
			(!this.hasPendingChanges && (this.templateType === TemplateTypeId.OPERATIONAL || this.editingPublishedNote) )
		);
	}
    
    private _mode: TocMode = TocMode.EDITOR;
	private readonly modeSubject = new Subject<TocMode>();
	readonly mode$ = this.modeSubject.asObservable();
	
    get mode() {
        return this._mode;
    }
    set mode(val) {
		if (val === this._mode) return;
		
        setTimeout(() => {
            if (this.tocTable)
                PrimeNGHelpersService.disableTreeTableDeselection(this.tocTable);
            
			this.updateTocScrollPosition();
        });
        
        this._mode = val;
		this.modeSubject.next(val);
    }
	
	get viewIsNarrow() {
		return this.noteEntryService.viewIsNarrow;
	}
	set viewIsNarrow(val) {
		this.noteEntryService.viewIsNarrow = val;
	}
    
    displayName?: string;
    displayPath?: string;
    
    _hasNextSection = false;
    get hasNextSection() {
        return this._hasNextSection || (this.editorMode === EditorMode.EDITING && !this.editingPublishedNote && this.canReviewNote);
    }
    
    _hasPreviousSection = false;
    get hasPreviousSection() {
        return this._hasPreviousSection || (this.templateType === TemplateTypeId.INSPECTION && this.editorMode === EditorMode.REVIEWING);
    }
    
    get enteringNote() {
        return this.mode !== TocMode.NAVIGATOR;
    }
    
    get isLoadingTemplate() {
        return this.noteEntryService.isLoadingTemplate;
    }
    
    get serviceIsReady() {
        return this.noteEntryService.isReady;
    }

	get editingPublishedNote() {
		return this.noteEntryService.editingPublishedNote;
	}

    constructor(
        public noteEntryService: NoteEntryService,
        private elRef: ElementRef,
		private logService: LogService,
        private cd: ChangeDetectorRef,
		private confirmationService: ConfirmationService,
		@Inject('$rootScope') 
		private $rootScope: LogBookRootScopeService) {
    }

    ngOnInit() {
	    const el = (this.elRef.nativeElement as HTMLElement).querySelector('.note-entry-container');
	    if (el) {
		    ResizeObserverHelper.observe(el).pipe(
				    takeUntil(this.destroySubject),
			        filter(event => event.contentRect.width > 0 && !(event.target as HTMLElement).hidden)
			    )
			    .subscribe(event => {
				    this.viewIsNarrow = event.contentRect.width < 768;
			    });
	    }
		
        combineLatest([
            this.noteEntryService.selectedLog$,
            this.noteEntryService.selectedTemplate$.pipe(tap(() => this.showNowPublishedMessage = false)),
            this.noteEntryService.selectedGroup$,
            this.noteEntryService.logs$
        ])
            .pipe(
                takeUntil(this.destroySubject)
            )
            .subscribe(([log, template, group, logs]) => {
				if (template && template.templateType.id === TemplateTypeId.OPERATIONAL && this.noteEntryService.treeNodeMap.nodes.length) {
					this.mode = TocMode.TOC;
				}
				else {
	                this.mode = template ? TocMode.EDITOR : TocMode.NAVIGATOR;
				}
				
                this.displayPath = `${this.selectedLog?.name ?? ''}\n${this.selectedTemplate?.name ?? ''}`
            });
    
        this.noteEntryService.start(this.startConfig);
        
        this.noteEntryService.selectedGroup$.pipe(takeUntil(this.destroySubject)).subscribe(group => {
            this._hasPreviousSection = !!this.noteEntryService.getNextSection('PREVIOUS');
            this._hasNextSection = !!this.noteEntryService.getNextSection('NEXT');
            
            this.showSections = false;
            
            if (group) {
                this.mode = TocMode.EDITOR;
                this.editorMode = EditorMode.EDITING;
                this._selectedNode = this.noteEntryService.treeNodeMap.map[group.segmentId ?? -1];
                
                // PrimeNG TreeTable requires source update to update the UI
                // Only updates if nodes were expanded
                if (this._selectedNode?.expandUp()) {
                    this.noteEntryService.treeNodeMap.nodes = [...this.noteEntryService.treeNodeMap.nodes];
                }
                setTimeout(() => this.updateEditorScrollPosition());
            }
			else {
				delete this._selectedNode;
            }
            
            
            let workingNode = this.selectedNode;
            let editingPath: string[] = [];
            while (workingNode) {
                editingPath.splice(0, 0, workingNode.label ?? '');
                workingNode = workingNode.parent;
            }
            
            this.displayName = editingPath.join(' > ');
        });

		this.noteEntryService.editingNote$.pipe(
			takeUntil(this.destroySubject)
		)
		.subscribe(note => {
			this.restrictSection = (note && note.subTemplateId && this.editingPublishedNote) ? note.subTemplateId : undefined;
		})

		const eventStopChange = ((event: IAngularEvent) => {
			const goToUrl = this.noteEntryService.$location.url();
			
			if (!this.hasPendingChanges)
				return;
			
			event.preventDefault();

			this.$rootScope.$broadcast('$routeChangeError');

			this.displayNavigationGuard(() => {
				this.hasPendingChanges = false;
				this.noteEntryService.$location.url(goToUrl);
			})
			
		})
		.bind(this);
		
		merge(
			LogBookRootScopeService.observeEvent(this.$rootScope, '$routeChangeStart'),
			LogBookRootScopeService.observeEvent(this.$rootScope, '$locationChangeStart')
		)
			.pipe(
				filter(() => this.trapOnNavigation)
			)
			.subscribe(([event]) => eventStopChange(event));

		this.logService.logChangeStart$.pipe(
			takeUntil(this.destroySubject)
		)
		.subscribe(changeLog => {
			if (this.hasPendingChanges) {
				this.displayNavigationGuard(() => {
					this.hasPendingChanges = false;
					changeLog();
				});
				return;
			}
			changeLog();
		});
		
		this.noteEntryService.noteStatusChangeSubject.pipe(
			takeUntil(this.destroySubject)
		)
			.subscribe(status => {
				switch (status) {
					case NoteStatusUpdate.ACCESS_REVOKED:
						this.accessRevokedVisible = true;
						break;
					case NoteStatusUpdate.PUBLISHED:
						this.showNowPublishedMessage = true;
						break;
				}
			});
    }

    ngAfterViewInit() {}

    ngOnDestroy() {
        this.destroySubject.next();
    }

	@HostListener('window:beforeunload', ['$event'])
	private windowBeforeUnload($event: BeforeUnloadEvent) {
		if (!this.hasPendingChanges || !AppStateService.currentUser ) {
			return;
		}
		const confirmMessage = 'There are pending changes to the note. Are you sure you want to leave?';
		$event.returnValue = confirmMessage;
		return confirmMessage;
	}

	displayNavigationGuard(onAccept: () => void, header?: string, message?: string) {
		header ??= 'Confirm Navigation';
		message ??= 'There are pending changes to the note. Are you sure you want to leave?';

		if (this.displayingNavModal)
				return;

		this.displayingNavModal = true;
		
		this.confirmationService.confirm({
			key: this.navigationGuardKey,
			header: header,
			message: message,
			defaultFocus: 'reject',
			accept: () => {
				// Ensure the dialog is closed before performing actions that could possibly destroy the component
				// timeout is set to let the confirmationService process.
				this.confirmationService.close();
				
				setTimeout(() => onAccept());
			}
		})
	}
    
    setSelectedNode(node: SegmentTreeNode) {
		if (node === this.selectedNode) {
			// Return to editor mode
			this.mode = this.TocModes.EDITOR;
			this.editorMode = this.EditorModes.EDITING;
			this.showSections = false;
			return;
		}
		
		if (node.data.id === this.restrictSection || (node === this.selectedNode && this.editorMode !== this.EditorModes.REVIEWING)) return;
		
        node = node ? this.noteEntryService.findNextBestNode(node) ?? node : node;
		(this.hasPendingChanges && this.templateType === TemplateTypeId.OPERATIONAL) 
			? this.displayNavigationGuard(setNode.bind(this), undefined, 'There are pending changes to this note. ' +
					'If you change sub template you will lose your current progress. Are you sure you want to change sub template?') 
			: setNode.call(this);

		function setNode(this: NoteEntryTocComponent) {
			this.noteEntryService.selectGroup(this.noteEntryService.groupsMap[(node.data.id)]);
		}
    }
    
    changeGroup(direction: 'NEXT' | 'PREVIOUS', scrollToc = true) {
        if (direction === 'NEXT' && !this._hasNextSection && this.editorMode === EditorMode.EDITING && this.canReviewNote) {
            this.reviewNote();
        }
        else if (direction === 'PREVIOUS' && this.editorMode === EditorMode.REVIEWING) {
            this.editorMode = EditorMode.EDITING;
        }
        else
            this.noteEntryService.changeGroup(direction);
        
        if (scrollToc) {
            setTimeout(() => this.updateTocScrollPosition());
        }
    }
    
    stopEditingNote(completion?: Function) {
		const onAccept = () => {
			this.selectedTemplate = undefined;
        	this.mode = TocMode.NAVIGATOR;
			completion?.();
		}
		if (this.hasPendingChanges) {
			this.displayNavigationGuard(
				onAccept,
				'Unsaved Changes',
				'Are you sure you want to stop editing this note? Any unsaved changes will be lost.'
			);
		}
		else
			onAccept();
    }
    
    goBack() {
		const updateBackPressed = () => this.backClicked.emit(this.mode);
        if (this.isLoadingTemplate || !this.viewIsNarrow || this.mode === TocMode.TOC || (this.mode === TocMode.EDITOR && this.noteEntryService.treeNodeMap.nodes.length === 0)) {
            this.stopEditingNote(updateBackPressed);
        }
        else if (this.mode === TocMode.EDITOR) {
            this.mode = TocMode.TOC;
			updateBackPressed();
        }
    }
    
    updateTocScrollPosition() {
	    const host = (this.elRef.nativeElement as HTMLElement | undefined);
		const container = host?.querySelector('.toc-container') as HTMLElement;
        const element = host?.querySelector(`.toc-node-${this.selectedNode?.data?.id ?? -1}`) as HTMLElement;
		
		if (!element) return;
		
		if (this.viewIsNarrow) {
			element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
			return;
		}
		
		if (!container || !element) return;
		container.scrollTo({ top: element.offsetTop, behavior: 'smooth' });
    }
    
    reviewNote() {
        if (this.templateType === TemplateTypeId.OPERATIONAL)
            return;
        
        this.showSections = false;
        this.mode = TocMode.EDITOR;
        this.editorMode = EditorMode.REVIEWING;
        this.noteEntryService.triggerSync(AutoSyncType.FORCE);
		this.updateEditorScrollPosition();
    }
	
	showErrorFeedback(event: MouseEvent) {
		if (!this.syncErrorFeedback || !(event.shiftKey || event.metaKey || AppStateService.isAdmin || AppStateService.isSwitchedUser)) return;
		this.syncFeedbackVisible = true;
		try {
			this.syncObjectJSON = JSON.stringify(this.syncErrorFeedback.object, null, 2)
		}
		catch (e: any) {
			this.syncObjectJSON = `Error Parsing error: ${e.message}`;
		}
	}
    
    publishNote(displayModalOnIssue: boolean) {
		// Process all validators and show error messages if any exist that haven't been shown yet
		this.noteEntryService.processAllValidators();
		
        if (displayModalOnIssue && this.noteHasIssues) {
            this.showFieldsMissing = true;
            return Promise.resolve();
        }
        
        this.showFieldsMissing = false;
        if (!this.publishAllowed)
            return Promise.reject('Publishing not allowed');
        
		this.publishingNote = true;
        return this.noteEntryService.syncNote(SyncType.PUBLISH)
			.pipe(
				switchMap((note) => {
					this.notePublished.emit(note);
					if (this.templateType === TemplateTypeId.INSPECTION || !this._hasNextSection)
                    	return this.noteEntryService.setToDefaultTemplate()
							.pipe(tap(() => this.editorMode = EditorMode.EDITING));

					this.noteEntryService.changeGroup('NEXT');
					return EMPTY.pipe(defaultIfEmpty(null));
				}),
				finalize(() => {
					this.publishingNote = false;
				})
			)
			.subscribe();
    }
	
	accessRevokeAcknowledged() {
		this.onAccessRevoked.next();
		this.accessRevokedVisible = false;
	}
	
	deleteNote(event: Event) {
		this.confirmationService.confirm({
			header: 'Delete Draft',
			message: 'Are you sure you want to delete this draft?',
			acceptButtonStyleClass: 'p-button-danger',
			target: event.target ?? undefined,
			accept: () => {
				this.noteEntryService.noteService.deleteNote(this.noteEntryService.editingNote!)
					.subscribe(() => {
						this.noteEntryService.setToDefaultTemplate()
							.subscribe(() => this.notePublished.emit(null));
					});
			}
		})
	}
    
    private updateEditorScrollPosition() {
		const el = (this.elRef.nativeElement as HTMLElement).querySelector('.entry-container') as HTMLElement;
		if (!el) return;
		
		if (this.viewIsNarrow) {
			el.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
		}
		else {
		    el.scrollTo({ behavior: 'smooth', top: 0 });
		}
    }
}